import logger from 'core/logger/logger';
import { CookieClientObject } from 'core/models/clients';

interface CookieOptions {
  path?: string;
  domain?: string;
  expires?: Date;
  secure?: boolean;
  samesite?: boolean;
  // use expires always to set expiry value
  // maxAge is being used for deleting action
  // maxAge -ve or 0 means delete cookies
  // here maxAge = -1 => delete cookie
  maxAge?: number;
}
class QuillLocalStorage {
  quillCookieCache: CookieClientObject;

  private KEYS_RESERVED_FOR_COOKIES = [
    'authenticated',
    'premium',
    'acceptedPremiumModesTnc',
    'theme',
    'userOnboardingScenario',
    'qdid',
    'useridtoken',
    'userTrackingAllowed',
    'anonID',
    'sidebarPinned',
  ];

  private isThisKeyReservedForCookiesOnly(key: string): boolean {
    return this.KEYS_RESERVED_FOR_COOKIES.includes(key);
  }

  private static instance: QuillLocalStorage;

  // a storage can be localstroage(true) or cookies(false)
  // each set, get, delete, copy function checks
  // type of storage and perform action accordingly
  // checks if browser and localstorage exist
  private isBrowserAndLocalstorage = false;

  // pattern will take care of `; name=<value>`
  // eslint-disable-next-line no-useless-escape
  private COOKIE_PATTERN = /([\.$?*|{}\(\)\[\]\\\/\+^])/g;

  private getNextYear() {
    const presentDate = new Date();
    presentDate.setFullYear(presentDate.getFullYear() + 1);
    return presentDate;
  }

  // convert options obj to valid string options for cookie
  private convertCookiesOptionsToString(options?: CookieOptions): string {
    let optionsStr = '';

    if (!options) {
      return `expires=${this.getNextYear().toUTCString()}; `;
    }

    // path if mentioned use that else use root
    optionsStr += `path=${options.path ? options.path : '/'}; `;

    // domain if mentioned used that
    if (options.domain) {
      optionsStr += `domain=${options.domain}; `;
    }

    // expires

    const expiryDate =
      options.expires && options.expires instanceof Date ? options.expires : this.getNextYear();
    optionsStr += `expires=${expiryDate.toUTCString()}; `;

    // age in number either expires or maxAge one should be used
    // > here max age specifically being used for deleting though
    // max-age <= 0 is considered deleting we are using -1
    if (options.maxAge) {
      optionsStr += `max-age=${options.maxAge}; `;
    }

    // http or https if absent both
    if (options.secure) {
      optionsStr += 'secure;';
    }

    return optionsStr;
  }

  // get value from cookie
  private getCookie(name: string) {
    const matches = document.cookie.match(
      new RegExp(`(?:^|; )${name.replace(this.COOKIE_PATTERN, '\\$1')}=([^;]*)`)
    );
    return matches ? decodeURIComponent(matches[1]) : null;
  }

  // get value for `name` key from storage
  private getFromStorage(name: string): string | null {
    if (!this.isThisKeyReservedForCookiesOnly(name) && this.isBrowserAndLocalstorage) {
      return localStorage.getItem(name);
    }
    return this.getCookie(name);
  }

  // set in cookies specifically and return complete set cookie
  // take care of any fomatting required encodeURIComponent
  // encodeURIComponent: encode everthing expect  A-Z a-z 0-9 - _ . ! ~ * ' ( )
  private setCookie(name: string, value: string, options?: CookieOptions) {
    const cookie = `${name}=${encodeURIComponent(value)}; ${this.convertCookiesOptionsToString(
      options
    )}`;
    document.cookie = cookie;
    return cookie;
  }

  // set to storage and return set value;
  // if browser support localstorage then use it else cookies
  private setToStorage(name: string, value: string, options?: CookieOptions): string {
    if (!this.isThisKeyReservedForCookiesOnly(name) && this.isBrowserAndLocalstorage) {
      localStorage.setItem(name, value);
      return value;
    }
    return this.setCookie(name, value, options);
  }

  // delelte fn for cookies
  private deleteCookie(name: string) {
    return this.setCookie(name, '', { maxAge: -1 });
  }

  private deleteFromStorage(name: string) {
    if (!this.isThisKeyReservedForCookiesOnly(name) && this.isBrowserAndLocalstorage) {
      return localStorage.removeItem(name);
    }
    return this.deleteCookie(name);
  }

  private getAllCookies() {
    const cookiesArray = `; ${document.cookie}`.split('; ');
    return cookiesArray.map((cookieValue) => {
      const [name, value] = cookieValue.split('=');
      return [name, decodeURIComponent(value)];
    });
  }

  private getAllLocalstorage() {
    const keyValue = [];
    for (let indexOfKey = 0; indexOfKey < localStorage.length; indexOfKey += 1) {
      const key = localStorage.key(indexOfKey);
      if (key) {
        const value = localStorage.getItem(key);
        keyValue.push([key, value]);
      }
    }
    return keyValue;
  }

  private getAllFromStorage() {
    if (this.isBrowserAndLocalstorage) {
      const localStorageKeys = this.getAllLocalstorage();
      const reservedKeys = this.getAllCookies().filter(([key]) =>
        this.isThisKeyReservedForCookiesOnly(key)
      );
      return [...localStorageKeys, ...reservedKeys];
    }
    return this.getAllCookies();
  }

  private constructor() {
    this.quillCookieCache = {};
    // check if browser and localstorage is supported
    // so that correct storage(locastorage|cookie) can be selected
    try {
      if (process.browser && localStorage) {
        // now localstorage is default storage
        this.isBrowserAndLocalstorage = true;
      }
    } catch (err) {
      logger('Cookies Disabled', err);
    }
  }

  static getInstance(): QuillLocalStorage {
    if (!QuillLocalStorage.instance) QuillLocalStorage.instance = new QuillLocalStorage();
    return QuillLocalStorage.instance;
  }

  /*   repopulateCache() {
    const cookiesArray = this.getAllCookies();
    if (cookiesArray && cookiesArray.length) {
      this.quillCookieCache = Object.fromEntries(cookiesArray);
    }
  } */

  set(cname: string, cvalue: string, options?: CookieOptions) {
    return this.setToStorage(cname, cvalue, options);
  }

  get(cname: string, defaultValue?: string): string {
    if (typeof window === 'undefined') return defaultValue || '';

    const localStorageValue = this.getFromStorage(cname);
    return localStorageValue || defaultValue || '';
  }

  delete(cname: string) {
    return this.deleteFromStorage(cname);
  }

  deleteMulti(cnameArray: Array<string>) {
    cnameArray.forEach((cname) => {
      this.deleteFromStorage(cname);
    });
  }

  getAll() {
    return this.getAllFromStorage();
  }
}

export default QuillLocalStorage;
